const TOKEN_KEY = "es_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Thrown when a request exceeds its `timeoutMs`. Distinct from a caller-driven
// abort (which rejects with the native AbortError) so callers can tell "the
// server was too slow" apart from "the user cancelled".
export class TimeoutError extends Error {
  constructor(message = "Request timed out") {
    super(message);
    this.name = "TimeoutError";
  }
}

// A non-2xx response. Carries the HTTP status and the PARSED FastAPI `detail`
// (a string, or a structured object like { error, message, dependents }) so
// callers can branch on `err.detail` — e.g. show the dependents of a 409
// "linked_dependents" instead of a generic toast. `message` is the best human
// string (detail.message / the string detail), so plain `err.message` callers
// keep working and no longer see "[object Object]".
export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown) {
    const message =
      typeof detail === "string"
        ? detail
        : detail && typeof detail === "object" &&
            typeof (detail as { message?: unknown }).message === "string"
          ? (detail as { message: string }).message
          : `Request failed: ${status}`;
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

// Per-call controls. `signal` lets a caller cancel in flight (e.g. a "skip"
// button); `timeoutMs` arms a client-side deadline so a hung endpoint can't
// leave the request pending forever. Both are optional and compose.
export type RequestOpts = { signal?: AbortSignal; timeoutMs?: number };

async function request<T>(
  path: string,
  options: RequestInit = {},
  opts: RequestOpts = {},
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (options.body && typeof options.body === "string") {
    headers["Content-Type"] = "application/json";
  }

  // Drive the fetch off our own controller so a timeout and a caller-supplied
  // signal both abort it; track which fired so the error is accurate.
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  if (opts.signal) {
    if (opts.signal.aborted) controller.abort();
    else opts.signal.addEventListener("abort", abortFromCaller, { once: true });
  }
  let timedOut = false;
  const timer =
    opts.timeoutMs && opts.timeoutMs > 0
      ? setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, opts.timeoutMs)
      : undefined;

  let res: Response;
  try {
    res = await fetch(path, { ...options, headers, signal: controller.signal });
  } catch (err) {
    // A timeout abort and a caller abort both surface here as an AbortError;
    // re-throw the timeout as a TimeoutError so it reads distinctly upstream.
    if (timedOut) throw new TimeoutError(`Request timed out after ${opts.timeoutMs}ms`);
    throw err;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    if (opts.signal) opts.signal.removeEventListener("abort", abortFromCaller);
  }

  if (res.status === 401) {
    const hadToken = Boolean(token);
    clearToken();
    // Hard-redirect only when an actual session expired. Tokenless 401s
    // (e.g. the organization fetch on the login screen) must just reject —
    // redirecting on those reloads /login in an infinite loop.
    if (hadToken && !window.location.pathname.startsWith("/login")) {
      // Preserve where the user was so they can be returned there after
      // re-authenticating (see the `login` route in App.tsx).
      const next = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      window.location.href = `/login?next=${next}`;
    }
    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new ApiError(res.status, body?.detail ?? body);
  }

  // A successful 204 intentionally has no response body. Attempting res.json()
  // here turns completed DELETE requests into client-side failures.
  if (res.status === 204) return undefined as T;

  return res.json();
}

export const api = {
  get: <T>(path: string, opts?: RequestOpts) => request<T>(path, {}, opts),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    }),

  // Multipart POST (file upload). `request` leaves a FormData body untouched, so
  // the browser sets the multipart boundary; the Authorization header still applies.
  postForm: <T>(path: string, form: FormData) =>
    request<T>(path, { method: "POST", body: form }),

  patch: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  put: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  delete: <T>(path: string) =>
    request<T>(path, { method: "DELETE" }),
};

/** Authenticated binary download. Kept separate from the JSON client so a PDF
 * response is never accidentally parsed as JSON. */
export async function downloadFile(path: string, fallbackName: string) {
  const token = getToken();
  const response = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ detail: response.statusText }));
    throw new ApiError(response.status, body?.detail ?? body);
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const filename = encoded ? decodeURIComponent(encoded) : plain || fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
